// Travel post image helpers: client-side compression, dual-file upload
// (full + thumbnail), and cleanup. Storage bucket: travel-images (public).
// Naming convention: <uid>/<timestamp>_<i>.jpg and <uid>/<timestamp>_<i>_thumb.jpg —
// thumbnails are derived from the full URL, only full URLs are stored in DB.
import { supabase } from './supabaseClient';

export const MAX_TRAVEL_IMAGES = 5;

const BUCKET = 'travel-images';

// Downscale an image file on a canvas. Returns a JPEG Blob.
export const compressImage = (file, maxEdge, quality) => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('圖片壓縮失敗'))),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('圖片讀取失敗'));
    };
    img.src = objectUrl;
  });
};

// Upload files (already validated) as full + thumb pairs.
// Returns the array of full-size public URLs to store in travel_posts.image_urls.
export const uploadTravelImages = async (userId, files) => {
  const urls = [];
  const ts = Date.now();
  for (let i = 0; i < files.length; i++) {
    const [full, thumb] = await Promise.all([
      compressImage(files[i], 1600, 0.8),
      compressImage(files[i], 400, 0.7)
    ]);
    const basePath = `${userId}/${ts}_${i}`;
    const uploads = [
      supabase.storage.from(BUCKET).upload(`${basePath}.jpg`, full, { contentType: 'image/jpeg' }),
      supabase.storage.from(BUCKET).upload(`${basePath}_thumb.jpg`, thumb, { contentType: 'image/jpeg' })
    ];
    const results = await Promise.all(uploads);
    const failed = results.find(r => r.error);
    if (failed) throw failed.error;
    const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(`${basePath}.jpg`);
    urls.push(publicUrl);
  }
  return urls;
};

// Derive the thumbnail URL from a stored full-size URL.
export const getThumbUrl = (fullUrl) =>
  fullUrl.replace(/\.jpg$/, '_thumb.jpg');

// Extract the storage object path from a public URL.
const pathFromUrl = (url) => {
  const marker = `/object/public/${BUCKET}/`;
  const idx = url.indexOf(marker);
  return idx === -1 ? null : decodeURIComponent(url.slice(idx + marker.length).split('?')[0]);
};

// Remove full images and their thumbnails. Best-effort: log failures but
// don't block the caller (orphan files are harmless, post deletion must succeed).
export const deleteTravelImages = async (urls) => {
  const paths = (urls || [])
    .map(pathFromUrl)
    .filter(Boolean)
    .flatMap(p => [p, p.replace(/\.jpg$/, '_thumb.jpg')]);
  if (paths.length === 0) return;
  const { error } = await supabase.storage.from(BUCKET).remove(paths);
  if (error) console.error('刪除旅伴貼文圖片失敗:', error);
};
